import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { getTheme } from './useDarkMode';

export type ThemeId = 
  | 'orange-red' | 'midnight-gold' | 'emerald-white' | 'rose-black' 
  | 'navy-coral' | 'purple-white' | 'teal-cream' | 'slate-orange'
  | 'indigo-pink' | 'forest-gold' | 'ocean-sand' | 'berry-cream'
  | 'charcoal-lime' | 'cobalt-silver' | 'mocha-cream' | 'ruby-black'
  | 'sapphire-gold' | 'mint-dark' | 'sunset-purple' | 'graphite-cyan';

export type NavbarStyle = 'transparent-scroll' | 'solid' | 'glass' | 'dark';

interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  bg: string;
  text: string;
}

interface Theme {
  id: ThemeId;
  name: string;
  colors: ThemeColors;
}

const THEMES: Record<ThemeId, Theme> = {
  'orange-red': {
    id: 'orange-red',
    name: 'Sunset Blaze',
    colors: { primary: '#FF5C00', secondary: '#FF1744', accent: '#FF8A00', bg: '#FFFFFF', text: '#1F2937' },
  },
  'midnight-gold': {
    id: 'midnight-gold',
    name: 'Midnight Gold',
    colors: { primary: '#0A0A0A', secondary: '#D4AF37', accent: '#FFD700', bg: '#0F0F0F', text: '#FFFFFF' },
  },
  'emerald-white': {
    id: 'emerald-white',
    name: 'Emerald Fresh',
    colors: { primary: '#059669', secondary: '#10B981', accent: '#34D399', bg: '#FFFFFF', text: '#064E3B' },
  },
  'rose-black': {
    id: 'rose-black',
    name: 'Rose Noir',
    colors: { primary: '#BE185D', secondary: '#1F2937', accent: '#F472B6', bg: '#FFFFFF', text: '#1F2937' },
  },
  'navy-coral': {
    id: 'navy-coral',
    name: 'Nautical Coral',
    colors: { primary: '#1E3A5F', secondary: '#FF6B6B', accent: '#4ECDC4', bg: '#F8FAFC', text: '#1E3A5F' },
  },
  'purple-white': {
    id: 'purple-white',
    name: 'Royal Purple',
    colors: { primary: '#7C3AED', secondary: '#A78BFA', accent: '#C4B5FD', bg: '#FFFFFF', text: '#5B21B6' },
  },
  'teal-cream': {
    id: 'teal-cream',
    name: 'Teal Cream',
    colors: { primary: '#0D9488', secondary: '#14B8A6', accent: '#FEF3C7', bg: '#FFFBEB', text: '#134E4A' },
  },
  'slate-orange': {
    id: 'slate-orange',
    name: 'Slate Fire',
    colors: { primary: '#475569', secondary: '#F97316', accent: '#FB923C', bg: '#F8FAFC', text: '#334155' },
  },
  'indigo-pink': {
    id: 'indigo-pink',
    name: 'Indigo Pop',
    colors: { primary: '#4338CA', secondary: '#EC4899', accent: '#F472B6', bg: '#FFFFFF', text: '#3730A3' },
  },
  'forest-gold': {
    id: 'forest-gold',
    name: 'Forest Gold',
    colors: { primary: '#14532D', secondary: '#166534', accent: '#D4AF37', bg: '#F0FDF4', text: '#14532D' },
  },
  'ocean-sand': {
    id: 'ocean-sand',
    name: 'Ocean Sand',
    colors: { primary: '#0369A1', secondary: '#0EA5E9', accent: '#FDE68A', bg: '#FEFCE8', text: '#0C4A6E' },
  },
  'berry-cream': {
    id: 'berry-cream',
    name: 'Berry Cream',
    colors: { primary: '#9F1239', secondary: '#E11D48', accent: '#FDE68A', bg: '#FFF1F2', text: '#881337' },
  },
  'charcoal-lime': {
    id: 'charcoal-lime',
    name: 'Charcoal Lime',
    colors: { primary: '#374151', secondary: '#1F2937', accent: '#A3E635', bg: '#111827', text: '#F3F4F6' },
  },
  'cobalt-silver': {
    id: 'cobalt-silver',
    name: 'Cobalt Silver',
    colors: { primary: '#2563EB', secondary: '#3B82F6', accent: '#C0C0C0', bg: '#EFF6FF', text: '#1E40AF' },
  },
  'mocha-cream': {
    id: 'mocha-cream',
    name: 'Mocha Cream',
    colors: { primary: '#78350F', secondary: '#92400E', accent: '#FEF3C7', bg: '#FFFBEB', text: '#451A03' },
  },
  'ruby-black': {
    id: 'ruby-black',
    name: 'Ruby Black',
    colors: { primary: '#9F1239', secondary: '#000000', accent: '#F43F5E', bg: '#0A0A0A', text: '#FFFFFF' },
  },
  'sapphire-gold': {
    id: 'sapphire-gold',
    name: 'Sapphire Gold',
    colors: { primary: '#1E40AF', secondary: '#3B82F6', accent: '#FFD700', bg: '#EFF6FF', text: '#1E3A8A' },
  },
  'mint-dark': {
    id: 'mint-dark',
    name: 'Mint Dark',
    colors: { primary: '#14B8A6', secondary: '#0F766E', accent: '#5EEAD4', bg: '#134E4A', text: '#F0FDFA' },
  },
  'sunset-purple': {
    id: 'sunset-purple',
    name: 'Sunset Purple',
    colors: { primary: '#EA580C', secondary: '#9333EA', accent: '#FBBF24', bg: '#FFF7ED', text: '#7C2D12' },
  },
  'graphite-cyan': {
    id: 'graphite-cyan',
    name: 'Graphite Cyan',
    colors: { primary: '#374151', secondary: '#4B5563', accent: '#06B6D4', bg: '#1F2937', text: '#F9FAFB' },
  },
};

export function useTheme() {
  const [themeId, setThemeId] = useState<ThemeId>('orange-red');
  const [navbarStyle, setNavbarStyle] = useState<NavbarStyle>('transparent-scroll');
  const [loading, setLoading] = useState(true);

  const theme = THEMES[themeId];

  // Apply CSS variables
  const applyTheme = useCallback((colors: ThemeColors) => {
    const root = document.documentElement;
    const isDark = getTheme() === 'dark';
    
    // In dark mode, use light text colors for better visibility
    const textColor = isDark ? '#F9FAFB' : colors.text;
    const bgColor = isDark ? '#111827' : colors.bg;
    
    root.style.setProperty('--theme-primary', colors.primary);
    root.style.setProperty('--theme-secondary', colors.secondary);
    root.style.setProperty('--theme-accent', colors.accent);
    root.style.setProperty('--theme-bg', bgColor);
    root.style.setProperty('--theme-text', textColor);
    
    // Update gradient-primary class
    const style = document.getElementById('dynamic-theme-style') || document.createElement('style');
    style.id = 'dynamic-theme-style';
    style.textContent = `
      .gradient-primary {
        background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary}) !important;
      }
      .text-primary-gradient {
        background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary}) !important;
        -webkit-background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
        background-clip: text !important;
      }
      .border-theme {
        border-color: ${colors.primary} !important;
      }
      .bg-theme-light {
        background-color: ${colors.primary}15 !important;
      }
      .hover-theme:hover {
        background-color: ${colors.primary} !important;
        color: white !important;
      }
      /* Dark mode text fixes */
      .dark .theme-text-color {
        color: #F9FAFB !important;
      }
      .dark .theme-bg-color {
        background-color: #111827 !important;
      }
    `;
    if (!document.getElementById('dynamic-theme-style')) {
      document.head.appendChild(style);
    }
  }, []);

  // Load theme from Supabase
  useEffect(() => {
    const loadTheme = async () => {
      const { data } = await supabase
        .from('theme_settings')
        .select('theme_id, navbar_style')
        .eq('id', 'main')
        .maybeSingle();
      
      if (data) {
        const newThemeId = data.theme_id as ThemeId;
        const newNavbarStyle = data.navbar_style as NavbarStyle;
        setThemeId(newThemeId);
        setNavbarStyle(newNavbarStyle);
        applyTheme(THEMES[newThemeId].colors);
      }
      setLoading(false);
    };

    loadTheme();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('theme_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'theme_settings', filter: 'id=eq.main' },
        (payload) => {
          const newData = payload.new as { theme_id: ThemeId; navbar_style: NavbarStyle };
          if (newData) {
            setThemeId(newData.theme_id);
            setNavbarStyle(newData.navbar_style);
            applyTheme(THEMES[newData.theme_id].colors);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [applyTheme]);

  // Apply initial theme
  useEffect(() => {
    if (theme) {
      applyTheme(theme.colors);
    }
  }, [theme, applyTheme]);

  // Re-apply theme when dark mode changes
  useEffect(() => {
    const handleStorageChange = () => {
      if (theme) {
        applyTheme(theme.colors);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom dark mode toggle event
    const handleDarkModeToggle = () => {
      if (theme) {
        applyTheme(theme.colors);
      }
    };
    window.addEventListener('darkmode-toggle', handleDarkModeToggle);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('darkmode-toggle', handleDarkModeToggle);
    };
  }, [theme, applyTheme]);

  return {
    theme,
    themeId,
    navbarStyle,
    loading,
    colors: theme?.colors || THEMES['orange-red'].colors,
  };
}

export { THEMES };
export type { Theme, ThemeColors };